import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import EstimateLineItems from './createItemizedEstimate';
import { Estimate, ItemizedEstimate, LineItem, default_terms_and_conditions, EstimateWithItemsRequest } from './estimateType';
import { ProjectAndCustomer } from '../project/projectType';
import ProjectApi from '../project/projectApi';
import EstimateApi from './estimateApi';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import Spinner from '../components/ui/spinner';
import { FilePlus, Brain, Info } from 'lucide-react';
import { formatAddress, formatCurrency } from '../lib/global_function';

const CreateEstimatePage = () => {
  const breadcrumbs = [
    { label: 'Project', path: '/main/project' },
    { label: 'Estimate', path: '/main/project/estimate' },
    { label: 'Create', path: '/main/project/estimate/create' },
  ];

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTermsHelp, setShowTermsHelp] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<ProjectAndCustomer | null>(null);

  const defaultEstimateState = {
    name: '',
    description: '',
    terms_and_conditions: default_terms_and_conditions,
    time_to_complete_hr: 0,
    time_to_complete_day: 0,
    tax_rate: 0,
    discount_usd: 0,
  };

  const defaultLineItem = {
    id: Date.now(),
    code: '',
    description: '',
    unit_price_dollar: 0,
    quantity: 0,
    sub_total_dollar: 0,
    cost_type: 'all' as 'all' | 'labor' | 'material',
    duration_hour: 0,
    estimate_id: '',
    is_optional: false,
  };

  const [estimate, setEstimate] = useState(defaultEstimateState);
  const [lineItems, setLineItems] = useState<LineItem[]>([defaultLineItem]);

  const [totals, setTotals] = useState({
    dollars: 0,
    hours: 0,
    optionalDollars: 0,
    optionalHours: 0,
  });

  const [baseTotals, setBaseTotals] = useState({
    dollars: 0,
    hours: 0,
    optionalDollars: 0,
    optionalHours: 0,
  });
  const [projectsAndCustomers, setProjectsAndCustomers] = useState<ProjectAndCustomer[]>([]);

  // Fetch projects and customers on page load
  useEffect(() => {
    const fetchProjectsAndCustomers = async () => {
      try {
        const fetchedProjectsAndCustomers = await ProjectApi.getProjectsAndCustomers();
        setProjectsAndCustomers(fetchedProjectsAndCustomers);

        // Preselect the project passed from projectPage.tsx
        const projectFromState = location.state?.projectAndCustomer;
        if (projectFromState) {
          setSelectedProjectId(projectFromState.project_id.toString());
          setSelectedCustomer(projectFromState);
        }
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
      }
    };
    fetchProjectsAndCustomers();
  }, [location.state]);

  useEffect(() => {
    const baseTotal = baseTotals.dollars;
    const taxAmount = baseTotal * (estimate.tax_rate / 100);
    const finalTotal = baseTotal + taxAmount - (estimate.discount_usd || 0);
    setTotals((prevTotals) => ({
      ...prevTotals,
      dollars: finalTotal > 0 ? finalTotal : 0,
    }));
  }, [estimate.tax_rate, estimate.discount_usd, baseTotals.dollars]);

  // Handle project selection
  const handleProjectChange = (projectId: string) => {
    setSelectedProjectId(projectId);
    const selected = projectsAndCustomers.find(
      (projCust) => projCust.project_id.toString() === projectId.toString()
    );
    setSelectedCustomer(selected || null);
  };

  const handleTotalsChange = (newTotals: { dollars: number; hours: number; optionalDollars: number; optionalHours: number }) => {
    const baseTotal = newTotals.dollars;
    const taxAmount = baseTotal * (estimate.tax_rate / 100);
    const finalTotal = baseTotal + taxAmount - (estimate.discount_usd || 0);

    setBaseTotals(newTotals);
    setTotals({
      ...newTotals,
      dollars: finalTotal > 0 ? finalTotal : 0,
    });
  };

  const resetForm = () => {
    setEstimate(defaultEstimateState);
    setLineItems([{ ...defaultLineItem, id: Date.now() }]);
    setTotals({
      dollars: 0,
      hours: 0,
      optionalDollars: 0,
      optionalHours: 0,
    });
    setBaseTotals({
      dollars: 0,
      hours: 0,
      optionalDollars: 0,
      optionalHours: 0,
    });
    setMessage(null);
    setErrorMessage(null);
    setSelectedProjectId(null);
    setSelectedCustomer(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    setMessage('');
    setIsSubmitting(true);

    if (!selectedProjectId || !selectedCustomer) {
      setErrorMessage('Please select a project.');
      setIsSubmitting(false);
      return;
    }

    // Validate numerical values
    if (isNaN(estimate.time_to_complete_day) || isNaN(estimate.time_to_complete_hr)) {
      setErrorMessage('Please enter valid numerical values for time estimates.');
      setIsSubmitting(false);
      return;
    }

    if (estimate.time_to_complete_day < 0 || estimate.time_to_complete_hr < 0) {
      setErrorMessage('Time estimates cannot be negative.');
      setIsSubmitting(false);
      return;
    }

    if (estimate.time_to_complete_day > 365 || estimate.time_to_complete_hr > 23) {
      setErrorMessage('Time estimates exceed maximum allowed values.');
      setIsSubmitting(false);
      return;
    }

    // Validate numerical values
    if (isNaN(estimate.tax_rate) || isNaN(estimate.discount_usd)) {
      setErrorMessage('Please enter valid numerical values for tax rate or discount amount.');
      setIsSubmitting(false);
      return;
    }

    const total_dollar = lineItems.reduce((sum, item) => sum + item.sub_total_dollar, 0);
    if (total_dollar === 0) {
      setErrorMessage('Enter unit price and quantity.');
      setIsSubmitting(false);
      return;
    }

    try {
      // Create combined payload with estimate and itemized estimates
      const payload: EstimateWithItemsRequest = {
        name: estimate.name,
        description: estimate.description,
        terms_and_conditions: estimate.terms_and_conditions as any, // Type conversion - backend will handle
        project_id: parseInt(selectedProjectId),
        customer_id: parseInt(selectedCustomer.customer_id),
        time_to_complete_hr: estimate.time_to_complete_hr,
        time_to_complete_day: estimate.time_to_complete_day,
        tax_rate: estimate.tax_rate,
        discount_usd: estimate.discount_usd,
        itemized_estimates: lineItems.map((item) => ({
          code: item.code,
          description: item.description,
          unit_price_dollar: item.unit_price_dollar.toString(),
          sub_total_dollar: item.sub_total_dollar.toString(),
          cost_type: item.cost_type,
          duration_hour: item.duration_hour.toString(),
          quantity: item.quantity,
          is_optional: item.is_optional,
        })),
      };

      // Single API call to create estimate with items
      await EstimateApi.createEstimate(payload);
      setMessage('Estimate created successfully');

      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectAIAssistant = () => {
    navigate('/main/project/estimate/smartassistant', {
      state: {
        selectedProjectId: selectedProjectId,
        selectedCustomer: selectedCustomer
      }
    });
  };

  // Calculate tax and discount amounts for display
  const taxAmount = baseTotals.dollars * (estimate.tax_rate / 100);
  const discountAmount = estimate.discount_usd || 0;

  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6">
        <Breadcrumbs items={breadcrumbs} />

        <div className="mt-6">
          <h1 className="text-2xl font-semibold text-foreground mb-6">Create Estimate</h1>

          {/* Error Message */}
          {errorMessage && (
            <Alert variant="error" className="mb-6">
              {errorMessage}
            </Alert>
          )}

          {/* Success Message with Actions */}
          {message && (
            <div className="mb-6 space-y-4">
              <Alert variant="success">
                {message}
              </Alert>
              <div className="flex flex-wrap gap-3">
                <Button onClick={() => navigate('/main/project/estimate')}>
                  View All Estimates
                </Button>
                <Button variant="inverted" onClick={resetForm}>
                  Create Another Estimate
                </Button>
              </div>
            </div>
          )}

          {/* Show form even after success, but make it collapsible or hidden */}
          <div className={message ? 'hidden' : ''}>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Form Section - 2 columns */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Customer Information */}
                  {selectedCustomer && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-foreground">
                        <strong className="font-semibold">Customer:</strong>{' '}
                        {selectedCustomer.customer_first_name} {selectedCustomer.customer_last_name},{' '}
                        {formatAddress(
                          selectedCustomer.customer_address_1,
                          '',
                          selectedCustomer.customer_city,
                          selectedCustomer.customer_state,
                          selectedCustomer.customer_zip
                        )}
                        , {selectedCustomer.customer_email}, {selectedCustomer.customer_phone}
                      </p>
                    </div>
                  )}

                  {/* Project Dropdown */}
                  <div>
                    <Label htmlFor="selectedProject">
                      Select Project <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      id="selectedProject"
                      name="selectedProject"
                      value={selectedProjectId || ''}
                      onChange={(e) => handleProjectChange(e.target.value)}
                      required
                    >
                      <option value="">Select Project</option>
                      {projectsAndCustomers.map((projCust) => (
                        <option key={projCust.project_id} value={projCust.project_id}>
                          {projCust.name}
                          {projCust.project_description
                            ? ` - ${projCust.project_description.substring(0, 100)}`
                            : ''}
                        </option>
                      ))}
                    </Select>
                  </div>

                  {/* Smart AI Estimate Assistant Button */}
                  {selectedCustomer && (
                    <div>
                      <Button
                        type="button"
                        size="medium"
                        variant="inverted"
                        onClick={handleSelectAIAssistant}
                      >
                        <Brain size={20} />
                        Smart AI Estimate Assistant
                      </Button>
                    </div>
                  )}

                  {/* Estimate Details */}
                  <div className="p-6 bg-white border border-gray-200 rounded-lg space-y-4">
                    <h2 className="text-lg font-semibold text-foreground mb-4">Estimate Details</h2>

                    {/* Estimate Name */}
                    <div>
                      <Label htmlFor="estimateName">
                        Estimate Name <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="estimateName"
                        value={estimate.name}
                        placeholder="Enter Estimate Name"
                        onChange={(e) => setEstimate({ ...estimate, name: e.target.value })}
                        required
                      />
                    </div>

                    {/* Estimate Description */}
                    <div>
                      <Label htmlFor="estimateDescription">Estimate Description</Label>
                      <Input
                        id="estimateDescription"
                        value={estimate.description}
                        placeholder="Enter Estimate Description"
                        onChange={(e) => setEstimate({ ...estimate, description: e.target.value })}
                      />
                    </div>

                    {/* Estimated Time to Complete */}
                    <div>
                      <Label>Estimated Time to Complete</Label>
                      <div className="flex flex-wrap items-center gap-3 mt-2">
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min={0}
                            max={365}
                            step="1"
                            placeholder="0"
                            className="w-20"
                            value={estimate.time_to_complete_day === 0 ? '' : estimate.time_to_complete_day}
                            onChange={(e) => {
                              const inputValue = parseFloat(e.target.value);

                              if (isNaN(inputValue) || inputValue < 0) {
                                setEstimate({ ...estimate, time_to_complete_day: 0 });
                                return;
                              }

                              const boundedValue = Math.min(inputValue, 365);
                              setEstimate({ ...estimate, time_to_complete_day: boundedValue });
                            }}
                          />

                          <span className="text-sm text-gray-700">Days</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min={0}
                            max={23}
                            step="1"
                            placeholder="0"
                            className="w-20"
                            value={estimate.time_to_complete_hr === 0 ? '' : estimate.time_to_complete_hr}
                            onChange={(e) => {
                              const inputValue = parseFloat(e.target.value);

                              if (isNaN(inputValue) || inputValue < 0) {
                                setEstimate({ ...estimate, time_to_complete_hr: 0 });
                                return;
                              }

                              const boundedValue = Math.min(inputValue, 23);
                              setEstimate({ ...estimate, time_to_complete_hr: boundedValue });
                            }}
                          />

                          <span className="text-sm text-gray-700">Hours</span>
                        </div>
                      </div>
                    </div>

                    {/* Terms and Conditions */}
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Label htmlFor="termsAndConditions">
                          Estimate Terms and Conditions
                        </Label>
                        <button
                          type="button"
                          onClick={() => setShowTermsHelp(!showTermsHelp)}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <Info size={16} />
                        </button>
                      </div>
                      {showTermsHelp && (
                        <p className="text-xs text-gray-600 mb-2 p-2 bg-blue-50 rounded">
                          This template will be displayed in the estimate sent to the customer. You can update it with your content.
                          Note: There are three percentage values: (1) Deposit; (2) Commencement Payment; (3) Completion Payment.
                          Ensure the total equals 100%.
                        </p>
                      )}
                      <Textarea
                        id="termsAndConditions"
                        value={estimate.terms_and_conditions}
                        onChange={(e) => setEstimate({ ...estimate, terms_and_conditions: e.target.value })}
                        rows={5}
                        className="min-h-[120px]"
                      />
                    </div>

                    {/* Tax Rate and Discount */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="taxRate">Tax Rate (%)</Label>
                        <Input
                          id="taxRate"
                          type="number"
                          min={0}
                          max={99}
                          step="0.1"
                          placeholder="0"
                          value={estimate.tax_rate === 0 ? '' : estimate.tax_rate}
                          onChange={(e) => {
                            const inputValue = parseFloat(e.target.value);

                            // Allow blank entry and no negative numbers
                            if (isNaN(inputValue) || inputValue < 0) {
                              setEstimate({ ...estimate, tax_rate: 0 });
                              return;
                            }

                            const boundedValue = Math.min(inputValue, 99);
                            setEstimate({ ...estimate, tax_rate: boundedValue });
                          }}
                        />


                      </div>
                      <div>
                        <Label htmlFor="discount">Discount ($)</Label>
                        <Input
                          id="discount"
                          type="number"
                          min={0}
                          max={10000}
                          step="10"
                          placeholder="0"
                          value={estimate.discount_usd === 0 ? '' : estimate.discount_usd}
                          onChange={(e) => {
                            const inputValue = parseFloat(e.target.value);

                            // Allow blank and enforce minimum 0
                            if (isNaN(inputValue) || inputValue < 0) {
                              setEstimate({ ...estimate, discount_usd: 0 });
                              return;
                            }

                            const boundedValue = Math.min(inputValue, 10000);
                            setEstimate({ ...estimate, discount_usd: boundedValue });
                          }}
                        />


                      </div>
                    </div>
                  </div>

                  {/* Line Items */}
                  <div className="p-6 bg-white border border-gray-200 rounded-lg">
                    <h2 className="text-lg font-semibold text-foreground mb-4">Itemized Estimates</h2>
                    <EstimateLineItems
                      lineItems={lineItems}
                      setLineItems={setLineItems}
                      onTotalsChange={handleTotalsChange}
                    />
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-start">
                    <Button
                      type="submit"
                      size="large"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Spinner size="sm" fullScreen={false} className="size-5" />
                          Creating Estimate...
                        </>
                      ) : (
                        <>
                          <FilePlus size={20} />
                          Save Estimate
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Sticky Total Summary - 1 column */}
                <div className="lg:col-span-1">
                  <div className="sticky top-6 p-6 bg-gradient-to-br from-primary/5 to-primary/10 border-2 border-primary/30 rounded-lg shadow-sm">
                    <h3 className="text-lg font-semibold text-foreground mb-4">Estimate Summary</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-700">Subtotal:</span>
                        <span className="font-semibold">${formatCurrency(baseTotals.dollars.toFixed(2))}</span>
                      </div>
                      {estimate.tax_rate > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-700">Tax ({estimate.tax_rate}%):</span>
                          <span className="font-semibold">${formatCurrency(taxAmount.toFixed(2))}</span>
                        </div>
                      )}
                      {discountAmount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-700">Discount:</span>
                          <span className="font-semibold text-red-600">-${formatCurrency(discountAmount.toFixed(2))}</span>
                        </div>
                      )}
                      <div className="pt-3 border-t-2 border-primary/30">
                        <div className="flex justify-between items-center">
                          <span className="text-lg font-bold text-foreground">Total:</span>
                          <span className="text-2xl font-bold text-primary">
                            ${formatCurrency(totals.dollars.toFixed(2))}
                          </span>
                        </div>
                      </div>
                      {baseTotals.optionalDollars > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-300 text-xs text-gray-600">
                          <div className="flex justify-between">
                            <span>Optional items:</span>
                            <span>${formatCurrency(baseTotals.optionalDollars.toFixed(2))}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Required items:</span>
                            <span>${formatCurrency((baseTotals.dollars - baseTotals.optionalDollars).toFixed(2))}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>


              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateEstimatePage;